import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";
import { emptyVideo, Video } from "../inspection/Video";
import Language from "../translation/language";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";
import { CodeDetection, ConfidenceLevels, DetectionsAtTime, emptyVideoAnalysisDataResponse, GroupCodesWithMainCodes, PredictionDetails, PredictionGroup, TimestampPrediction, VideoAnalysisDataResponse } from "../../api-models/response/VideoAnalysisDataResponse";
import { FileStates } from "../shop/ShopHistoryStorage";
import { translationService } from "../translation/TranslationService";
import { featureFlagsStorage } from "../feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";
import { endpoints } from "../api/ApiEndpoints";
import serializeToQueryParams from "../../helpers/QueryParams";
import { ApplicationError, errorHandler } from "../error-handler/ErrorHandlerService";
import { sortHexBasedOnLuminance } from "../../helpers/ColorHelpers";
import { api } from "../api/ApiService";
import { uploadFilesService } from "../upload/UploadFilesService";
import { ActivityTypes } from "../shop/ActivityPricesStorage";
import { toJS } from "mobx";
import { SecondaryCodeEnum } from "../../generated/Damages";
import { ServiceModel } from "../app/ServiceStorage";
import { AppStorageModel } from "../app/AppStorage";

export const AnalysisServiceModel = types.compose(
    ServiceModel,
    types.model({
        language: types.enumeration<Language>(Object.values(Language)),
        analysisContent: types.optional(types.maybeNull(types.string), null),
        detectedMainCodes: types.optional(types.array(types.frozen<CodeDetection>()), []),
        detectedCharacterizations: types.optional(types.array(types.frozen<CodeDetection>()), []),
        isOverviewDisplayed: true,
        codesAvailableForDetection: types.optional(
            types.array(types.frozen<GroupCodesWithMainCodes>()),
            []
        ),
        vaData: types.optional(
            types.frozen<VideoAnalysisDataResponse>(),
            emptyVideoAnalysisDataResponse
        ),
        plotNeedsRelayout: false,
        analysisConfidenceOption: ConfidenceOptions.Recommended,
        firstTimeChoosingAnalysisIndividualConfidence: true,
        shouldShowAnalysisIndivididualConfidenceModal: false,
        analysisIndividualConfidenceLevels: types.optional(types.frozen<ConfidenceLevels>(), {}),
        analysisOverallConfidence: types.optional(types.number, 0.75),
    })
).views((self) => ({
    get mainCodeColors(): any {
        return self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat()
          .reduce((accumulator, value) => {
            return { ...accumulator, [value.mainCode]: value.color };
          }, {});
    },
    get allPredictions() {
        const flatted = self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat();
  
        let combinedPredictions: TimestampPrediction[] = [];
  
        for (const mainCode of flatted) {
          var confidence = 0;
          if (self.analysisConfidenceOption === ConfidenceOptions.Recommended) {
            confidence = self.vaData.recommendedConfidenceLevels[mainCode.mainCode]
          } else {
            if (self.analysisConfidenceOption === ConfidenceOptions.Individual) {
              if (self.analysisIndividualConfidenceLevels?.[mainCode.mainCode]) {
                confidence = self.analysisIndividualConfidenceLevels?.[mainCode.mainCode];
              }
            } else {
              confidence = self.analysisOverallConfidence
            }
          }
          
          if (confidence > 1) confidence = confidence / 100;
          var object = self.vaData.predictionsPerAccuracy[mainCode.mainCode + "_" + confidence];
          combinedPredictions = [...combinedPredictions, ...Object.values(object) as TimestampPrediction[]]
        }
  
        return combinedPredictions;
    },
    get predictionsForSmallestAccuracy() {
        const flatted = self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat();
  
        let combinedPredictions: TimestampPrediction[] = [];
  
        for (const mainCode of flatted) {
          combinedPredictions = [
            ...combinedPredictions,
            ...(Object.values(
              self.vaData.predictionsPerAccuracy[`${mainCode.mainCode}_${"0.65"}`]
            ) as TimestampPrediction[]),
          ];
        }
  
        return combinedPredictions;
      },
      get analysisNotStarted(): boolean {
        return self.status === FileStates.None;
      },
      get analysisFailed(): boolean {
        return self.status === FileStates.Failed;
      },
      get analysisRunning(): boolean {
        return self.status === FileStates.Running;
      },
      get analysisSuccessful(): boolean {
        return self.status === FileStates.Success;
      },
  
      get pauses() {
        return self.vaData.detectedTimestamps.map((x) => {
          const timestampIntervalCheck = self.vaData.pauses.find((p) => p.begin <= x && p.end >= x);
  
          if (timestampIntervalCheck) return 1;
          return 0;
        });
      },
  
      get allTimestamps() {
        return self.vaData.detectedTimestamps;
      },
}))
.actions((self) => ({
    getSortedCodesByColor: (codes: GroupCodesWithMainCodes[]): GroupCodesWithMainCodes[] => {
        const sortedCodes: GroupCodesWithMainCodes[] = [];
  
        codes.forEach((code) => {
          sortedCodes.push({
            groupCode: code.groupCode,
            color: code.color,
            mainCodesWithSecondaryCodes: code.mainCodesWithSecondaryCodes.sort((code1, code2) =>
              sortHexBasedOnLuminance(code1.color, code2.color)
            ),
          });
        });
  
        return sortedCodes;
    },

    isMainCodeDetected: (mainCode: string): boolean => {
        return self.predictionsForSmallestAccuracy.some(
          (x) => x.predictionDetails?.mainCode === mainCode
        );
    }
})).extend(self => ({
    actions: {
        clear() {
            self.fileId = undefined;
            self.video = undefined;
            self.status = FileStates.None
        },
        request: flow(function*() {
            const languageQueryParam = translationService.language === Language.english ? 0 : 1;
              if (featureFlagsStorage.isFeatureEnabled(Flags.Render_VA_Diagram_In_Platform)) {
                  const endpointVAData = endpoints.get("video_analysis_data");
                  if (endpointVAData) {
                      endpointVAData.routeParams = [self.fileId!];
                  }

                  var responseVAData:any = {};
                  try {
                      responseVAData = yield getEnv(self).api.get(endpointVAData);
                  } catch (error) {
                      self.video = new Video({...emptyVideo, isConverted: true, analysisSuccessfullyDone: false, segmentationSuccessfullyDone: false});
                  }

                  self.video = new Video(responseVAData.video);
                  self.status = self.getFileState(
                      responseVAData.videoAutoDetectionWasSuccessful, 
                      responseVAData.videoAutoDetectionErrorMessage,
                      responseVAData.videoAutoDetectionIsInProgress);
                  if (self.status !== FileStates.Success) return;
                  self.codesAvailableForDetection = cast(
                      self.getSortedCodesByColor(responseVAData.codesAvailableForDetection)
                  );
                  self.vaData = responseVAData;
                  self.analysisOverallConfidence =
                      self.vaData.recommendedConfidenceLevels["Video_Analysis"];

                  const mainCodes: CodeDetection[] = [];
                  self.codesAvailableForDetection.forEach((groupCode) => {
                      groupCode.mainCodesWithSecondaryCodes.forEach((mainCode) => {
                          mainCodes.push({
                              code: mainCode.mainCode,
                              colorCode: mainCode.color,
                              isVisible: true,
                              secondaryCodes: mainCode.secondaryCodes,
                              isDetected: self.isMainCodeDetected(mainCode.mainCode),
                              groupCode: groupCode.groupCode,
                          });
                      });
                  });

                  self.detectedMainCodes = cast(mainCodes);

                  const characterizationCodes: CodeDetection[] = [];
                  for (let key in self.vaData.characterizationCodesWithColors) {
                          characterizationCodes.push({
                          code: key,
                          colorCode: self.vaData.characterizationCodesWithColors[key],
                          isVisible: true,
                          isDetected: self.isMainCodeDetected(key),
                      });
                  }
                  self.detectedCharacterizations = cast(characterizationCodes);
              } else {
                  const endpoint = endpoints.get("media_file_video_analysis");
                  if (endpoint) {
                      endpoint.routeParams = [self.fileId!];
                      endpoint.queryParams = serializeToQueryParams({ language: languageQueryParam });
                  }
                  const response = yield getEnv(self).api.get(endpoint);
                  self.status = self.getFileState(
                      responseVAData.videoAutoDetectionWasSuccessful, 
                      responseVAData.videoAutoDetectionErrorMessage,
                      responseVAData.videoAutoDetectionIsInProgress);
                  self.analysisContent = response.analysisContent;
              }
        }),
        runService: flow(function* () {
            try {
                const endpoint = endpoints.get("media_file_video_analysis");
                if (endpoint) {
                    endpoint.routeParams = [self.fileId!];
                }
                self.status = FileStates.Running;
                yield getEnv(self).api.put(endpoint);
            } catch (error) {
                self.status = FileStates.None;
                const applicationError = error as ApplicationError;
                getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
                throw applicationError;
            }
        }),
        runAfterUpload: flow(function* () {
          yield self.startService();
          yield self.load();
        })
    }
})).actions((self) => {
    const exportVideoAnalysis = flow(function* () {
        try {
          self.setLoading(true);
          const endpoint = endpoints.get("media_file_analysis_export");
          if (endpoint) {
            endpoint.routeParams = [self.fileId!];
          }
          const response = yield getEnv(self).api.download(endpoint);
          self.setLoading(false);
          return response.data;
        } catch (error) {
          self.setLoading(false);
          const applicationError = error as ApplicationError;
          getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
          throw applicationError;
        }
    });

    return {
        async exportVideoAnalysis(){
          return await exportVideoAnalysis();
        },
        setLanguage(newLanguage: Language) {
            self.language = newLanguage;
        },
        setAnalysisConfidenceOption(confidenceOption: ConfidenceOptions) {
            self.analysisConfidenceOption = confidenceOption;
        },

        showAnalysisIndivididualConfidenceModal() {
            self.shouldShowAnalysisIndivididualConfidenceModal = true;
        },

        hideAnalysisIndivididualConfidenceModal() {
            self.shouldShowAnalysisIndivididualConfidenceModal = false;
        },
        setAnalysisOverallConfidence(overallConfidence: number) {
            self.analysisOverallConfidence = overallConfidence;
        },
    
        setIndividualAnalysisConfidence(confidenceLevels: ConfidenceLevels) {
            self.analysisIndividualConfidenceLevels = confidenceLevels;
            self.firstTimeChoosingAnalysisIndividualConfidence = false;
        },
        loadMainCodesDetected() {
            const videoAnalysisDiagram: any = window.document.getElementById(
              "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.get_main_codes_dic) {
              const mainCodeDetections = videoAnalysisDiagram.contentWindow.get_main_codes_dic();
              const mainCodes: CodeDetection[] = [];
    
              for (let key in mainCodeDetections) {
                mainCodes.push({ code: key, colorCode: mainCodeDetections[key], isVisible: true });
              }
    
              self.detectedMainCodes = cast(mainCodes);
            }
        },
        loadCharacterizationsDetected() {
            const videoAnalysisDiagram: any = window.document.getElementById(
              "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.get_characterizations_dic) {
              const characterizationDetections =
                videoAnalysisDiagram.contentWindow.get_characterizations_dic();
              const characterizations: CodeDetection[] = [];
    
              for (let key in characterizationDetections) {
                characterizations.push({ code: key, colorCode: characterizationDetections[key] });
              }
    
              self.detectedCharacterizations = cast(characterizations);
            }
        },
    
        changeView(isOverview: boolean) {
            self.isOverviewDisplayed = isOverview;
            const videoAnalysisDiagram: any = window.document.getElementById(
                "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.change_preview) {
                videoAnalysisDiagram.contentWindow.change_preview(isOverview);
            }
        },
    
        toggleMainCode(mainCode: string, isVisible: boolean) {
            const videoAnalysisDiagram: any = window.document.getElementById(
                "damages-video-analysis-iframe"
            );
            if (
                videoAnalysisDiagram &&
                videoAnalysisDiagram.contentWindow.toggle_main_code_visibility
            ) {
                videoAnalysisDiagram.contentWindow.toggle_main_code_visibility(mainCode, isVisible);
            }
        },
    
        addDamageToGraph(code: string, timestamp: number) {
            const videoAnalysisDiagram: any = window.document.getElementById(
                "damages-video-analysis-iframe"
            );
            if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.add_damage_to_graph) {
                videoAnalysisDiagram.contentWindow.add_damage_to_graph(code, timestamp);
            }
        },
    
        updateDamageSeriesIntoGraph(code: string, timestamps: number[]) {
            const videoAnalysisDiagram: any = window.document.getElementById(
            "damages-video-analysis-iframe"
            );
            if (
            videoAnalysisDiagram &&
            videoAnalysisDiagram.contentWindow.update_damage_series_into_graph
            ) {
            videoAnalysisDiagram.contentWindow.update_damage_series_into_graph(code, timestamps);
            }
        },
        getPredictionsByCode(mainCode: string) {
            var confidence = 0;
            if (self.analysisConfidenceOption === ConfidenceOptions.Recommended) {
              confidence = self.vaData.recommendedConfidenceLevels[mainCode]
            } else {
              if (self.analysisConfidenceOption === ConfidenceOptions.Individual) {
                if (self.analysisIndividualConfidenceLevels?.[mainCode]) {
                  confidence = self.analysisIndividualConfidenceLevels?.[mainCode];
                }
              } else {
                confidence = self.analysisOverallConfidence
              }
            }
            if (confidence > 1) confidence = confidence / 100;
            
            var object = self.vaData.predictionsPerAccuracy[mainCode + "_" + confidence];
            const predictions = Object.values(object) as TimestampPrediction[];
    
            return self.vaData.detectedTimestamps.map((x) => {
              const timestampPrediction = predictions.find((p) => p.timestamp === x);
    
              if (timestampPrediction) return timestampPrediction.predictionDetails.accuracy;
              return 0;
            });
        },
        changeMainCodeVisibility(mainCode: string) {
            let mainCodeDetections = toJS(self.detectedMainCodes);
            const index = mainCodeDetections.findIndex((det) => det.code === mainCode);
            mainCodeDetections[index] = {
              ...mainCodeDetections[index],
              isVisible: self.isMainCodeDetected(mainCode) ? !mainCodeDetections[index].isVisible : false,
            };
            self.detectedMainCodes = cast(mainCodeDetections);
    
            self.plotNeedsRelayout = true;
        },
        plotRelayoutDone() {
            self.plotNeedsRelayout = false;
        },
        buildPredictionGroupsForDetectedDamages(predictionGroups: PredictionGroup[]) {
            for (const prediction of self.allPredictions) {
              const codePrediction = prediction.predictionDetails;
              const hasMatchingMainCode = (x: PredictionGroup) =>
                x.mainCode === codePrediction.mainCode;
    
              const hasC1Code = (x: PredictionGroup) => x.hasC1Code && codePrediction.c1Code;
    
              const hasC2Code = (x: PredictionGroup) => x.hasC2Code && codePrediction.c2Code;
    
              const hasOnlyMainCode = (x: PredictionGroup) =>
                !x.hasC1Code && !codePrediction.c1Code && !x.hasC2Code && !codePrediction.c2Code;
    
              const predictionGroupC1 = predictionGroups.find(
                (x) => hasMatchingMainCode(x) && hasC1Code(x)
              );
              const predictionGroupC2 = predictionGroups.find(
                (x) => hasMatchingMainCode(x) && hasC2Code(x)
              );
              const predictionGroupMainCode = predictionGroups.find(
                (x) => hasMatchingMainCode(x) && hasOnlyMainCode(x)
              );
    
              if (codePrediction.c1Code) {
                predictionGroupC1?.predictions.push({
                  accuracy: codePrediction.accuracy,
                  secondaryCode: codePrediction.c1Code,
                  timestamp: prediction.timestamp,
                });
              }
    
              if (codePrediction.c2Code) {
                predictionGroupC2?.predictions.push({
                  accuracy: codePrediction.accuracy,
                  secondaryCode: codePrediction.c2Code,
                  timestamp: prediction.timestamp,
                });
              }
    
              if (!codePrediction.c1Code && !codePrediction.c2Code) {
                predictionGroupMainCode?.predictions.push({
                  accuracy: codePrediction.accuracy,
                  secondaryCode: "",
                  timestamp: prediction.timestamp,
                });
              }
            }
        },
        hasMainCodeOnly(predictionGroup: PredictionGroup) {
            return !predictionGroup.hasC1Code && !predictionGroup.hasC2Code;
        },
        characterizationAtTime(time: number): DetectionsAtTime {
            const detectedCharacterizations: any = {};
    
            const predictionsAtTime = self.allPredictions?.filter(
              (x) => Math.round(x.timestamp) === Math.round(time)
            );
    
            if (!predictionsAtTime) return {};
    
            let combinedPredictionsAtTime: PredictionDetails[] = [];
    
            for (const mainCode of predictionsAtTime) {
              if (mainCode.predictionDetails)
                combinedPredictionsAtTime = [
                  ...combinedPredictionsAtTime,
                  ...[mainCode.predictionDetails],
                ];
            }
    
            if (!combinedPredictionsAtTime?.length) return {};
    
            for (const prediction of combinedPredictionsAtTime) {
              detectedCharacterizations[prediction.mainCode] ??= {};
              if (prediction.c1Code)
                detectedCharacterizations[prediction.mainCode][
                  SecondaryCodeEnum[SecondaryCodeEnum.C1]
                ] = prediction.c1Code;
              if (prediction.c2Code)
                detectedCharacterizations[prediction.mainCode][
                  SecondaryCodeEnum[SecondaryCodeEnum.C2]
                ] = prediction.c2Code;
            }
            return detectedCharacterizations;
        },
        getSecondaryCodeName(predictionGroup: PredictionGroup) {
            if (this.hasMainCodeOnly(predictionGroup)) {
              return "MC";
            }
    
            if (predictionGroup.hasC1Code) {
              return SecondaryCodeEnum[SecondaryCodeEnum.C1];
            }
    
            return SecondaryCodeEnum[SecondaryCodeEnum.C2];
        },
    }
})

export type AnalysisService = Instance<typeof AnalysisServiceModel>;